import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import {
  setIsSearchTermTabDataFetched,
  setSearchTermTabLoader,
} from "../../../../../features/searchTermTabSlice";
import { STOperationEndPointLogic } from "../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import { useSearchParams } from "react-router-dom";

const QUERY_IDS = {
  SEARCH_TERM_WITHOUT_CLASSIFICATION: "5763513033162752",
  EXCLUSIVE_MISSING_CROSS_NEGATIVES: "5639087159508992",
  CONTRADICTING_SEARCH_TERM_DEFINITIONS: "5716929146781696",
  IDENTICAL_TO_EXACT_KEYWORD: "5715241090416640",
  CAPTURED_BY_MULTIPLE_KEYWORDS: "5737612971081728",
};

export const useSearchTermsTab = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const selectedSharedUid = searchParams.get("selectedSite") || "";
  const selectedAccountSharedUid = searchParams.get("account") || "";

  const fetchSearchTermsTabData = async () => {
    dispatch(setSearchTermTabLoader(true));
    dispatch(setIsSearchTermTabDataFetched(false));

    try {
      const [responseOfUnclasssifiedST] = await Promise.all([
        STOperationEndPointLogic.getSTPerformanceUnclassified(
          oauthTokenHeader,
          QUERY_IDS.SEARCH_TERM_WITHOUT_CLASSIFICATION,
          selectedSharedUid,
          selectedAccountSharedUid,
        ),
        STOperationEndPointLogic.getESTMissingCrossNegatives(
          oauthTokenHeader,
          QUERY_IDS.EXCLUSIVE_MISSING_CROSS_NEGATIVES,
          selectedSharedUid,
          selectedAccountSharedUid,
        ),
        STOperationEndPointLogic.getContradictingSTDefinitions(
          oauthTokenHeader,
          QUERY_IDS.CONTRADICTING_SEARCH_TERM_DEFINITIONS,
          selectedSharedUid,
          selectedAccountSharedUid,
        ),
        STOperationEndPointLogic.getIdenticalToExactKeyword(
          oauthTokenHeader,
          QUERY_IDS.IDENTICAL_TO_EXACT_KEYWORD,
          selectedSharedUid,
          selectedAccountSharedUid,
        ),

        STOperationEndPointLogic.getCapturedByMultipleKeywords(
          oauthTokenHeader,
          QUERY_IDS.CAPTURED_BY_MULTIPLE_KEYWORDS,
          selectedSharedUid,
          selectedAccountSharedUid,
        ),
      ]);

      const allResponses = {
        responseOfUnclasssifiedST: responseOfUnclasssifiedST,
      };

      return allResponses;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch search terms data. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setSearchTermTabLoader(false));
      dispatch(setIsSearchTermTabDataFetched(true));
    }
  };

  // Function to create positive search term links
  // const createPositiveSearchTermLinks = async (
  //   positiveDataList: any[],
  //   addNegative: boolean,
  //   type: "Exclusive" | "Allowed",
  // ) => {
  //   let successCount = 0;
  //   let failCount = 0;

  //   for (const item of positiveDataList) {
  //     try {
  //       const body = {
  //         search_term: item.search_term,
  //         search_term_match_type: item.search_term_match_type,
  //         keyword_id: item.keyword_id,
  //         scope_shared_uid: item.scope_shared_uid,
  //         link_type: type,
  //         add_negative_to_other_ad_groups: addNegative,
  //       };

  //       const result = await STOperationEndPointLogic.createPositiveLinks(
  //         oauthTokenHeader,
  //         body,
  //       );

  //       if (result?.reason === "Success") {
  //         successCount++;
  //         const negatives = result.applied_scope_negatives || 0;
  //         const negativeInfo =
  //           negatives > 0 ? ` (${negatives} negatives applied)` : "";

  //         dispatch(
  //           setDisplayErrorSnackBar({
  //             message: `Successfully added "${item.search_term}" as ${type}${negativeInfo}`,
  //             type: "success",
  //           }),
  //         );
  //       } else {
  //         failCount++;
  //         dispatch(
  //           setDisplayErrorSnackBar({
  //             message: `Failed to add "${item.search_term}" as ${type}`,
  //             type: "error",
  //           }),
  //         );
  //       }
  //     } catch (error) {
  //       failCount++;
  //       const errorMessage =
  //         error instanceof Error ? error.message : "Unknown error";
  //       dispatch(
  //         setDisplayErrorSnackBar({
  //           message: `Error adding "${item.search_term}" as ${type}: ${errorMessage}`,
  //           type: "error",
  //         }),
  //       );
  //     }
  //   }

  //   return { successCount, failCount };
  // };

  const createPositiveSearchTermLinksMulti = async (reqBody: any) => {
    let successCount = 0;
    let failCount = 0;
    const messages: { message: string; type: string }[] = []; //

    try {
      const response = await STOperationEndPointLogic.createPositiveLinksMulti(
        oauthTokenHeader,
        reqBody,
      );

      if (response?.status === 201) {
        const results = response?.data?.data?.results || [];
        successCount = results.length;
        messages.push({
          //push instead of dispatch
          message: `Successfully added ${results.length} positive search terms`,
          type: "success",
        });
      }

      if (response?.status === 207) {
        const results = response?.data?.data?.results || [];
        const summary = response?.data?.data?.summary || {};

        const successfulTerms = results.filter(
          (r: any) => r.status === "success",
        );
        const failedTerms = results.filter((r: any) => r.status === "failure");

        if (summary?.success > 0) {
          successCount = summary.success;
          messages.push({
            message:
              successfulTerms.length === 1
                ? `Successfully added "${successfulTerms[0]?.term_data?.search_term}" as ${successfulTerms[0]?.term_data?.link_type}`
                : `Successfully added ${successfulTerms.length} positive search terms`,
            type: "success",
          });
        }

        if (summary?.failure > 0) {
          failCount = summary.failure;
          messages.push({
            message:
              failedTerms.length === 1
                ? `Failed to add "${failedTerms[0]?.term_data?.search_term}" — ${failedTerms[0]?.reason}`
                : `Failed to add ${failedTerms.length} positive search terms`,
            type: "error",
          });
        }
      }
    } catch (error: any) {
      const parsedError = JSON.parse(error?.text || "{}");
      const results = parsedError?.data?.results || [];
      const summary = parsedError?.data?.summary || {};
      console.error("Error adding positive terms:", error);

      if (results.length > 0) {
        failCount = summary?.failure ?? results.length;

        const uniqueErrors = [...new Set(results.map((r: any) => r.reason))];
        const errorMessage = uniqueErrors.join(" | ");

        messages.push({
          message:
            results.length === 1
              ? `Failed to add "${results[0]?.term_data?.search_term}" as positive — ${errorMessage}`
              : `Failed to add ${results.length} positive terms — ${errorMessage}`,
          type: "error",
        });
      } else {
        // fallback for non-API errors
        failCount = summary?.failure;

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to add positive terms.";
        messages.push({
          message: `Error adding positive terms: ${errorMessage}`,
          type: "error",
        });
      }
    }

    return { successCount, failCount, messages }; // return messages array
  };

  // Function to create negative search term links
  const createNegativeSearchTermLinks = async (reqBody: any) => {
    let successCount = 0;
    let failCount = 0;
    let failedSearchTermUids: string[] = []; // Add this to track failed UIDs
    const messages: { message: string; type: string }[] = [];
    // const testBody = {
    //   term: "classification02",
    //   match_type: "EXACT",
    //   level: "ad_group",
    //   level_shared_uid: "93b9bb62a7a545179660ea1c0718814b",
    // };

    // const newRwqBody = {
    //   terms: [...reqBody.terms, testBody],
    // };

    try {
      const response =
        await STOperationEndPointLogic.addNegativeTermsToAdgroupUpdate(
          oauthTokenHeader,
          reqBody,
        );

      if (response?.status === 200) {
        const results = response?.data?.data?.results || [];
        successCount = results.length;
        messages.push({
          message: `Successfully added ${results.length} negative search terms`,
          type: "success",
        });
      }

      if (response?.status === 207) {
        const results = response?.data?.data?.results || [];
        const summary = response?.data?.data?.summary || {};

        const successfulTerms = results.filter(
          (r: any) => r.status === "success",
        );
        const failedTerms = results.filter((r: any) => r.status === "failed");
        if (summary?.successful > 0) {
          successCount = summary.successful;
          messages.push({
            message:
              successfulTerms.length === 1
                ? `Successfully added "${successfulTerms[0]?.term}" as negative`
                : `Successfully added ${successfulTerms.length} negatives search terms`,
            type: "success",
          });
        }

        if (summary?.failed > 0) {
          failCount = summary.failed;
          // Extract search term UIDs of failed terms
          failedSearchTermUids = failedTerms
            .map((failedTerm: any) => {
              const originalTerm = reqBody.terms.find(
                (t: any) => t.term === failedTerm.term,
              );
              return originalTerm?.additional_info?.search_term_shared_uid;
            })
            .filter(Boolean);

          messages.push({
            message:
              failedTerms.length === 1
                ? `Failed to add "${failedTerms[0]?.term_data?.search_term}" — ${failedTerms[0]?.reason}`
                : `Failed to add ${failedTerms.length} negative search terms`,
            type: "error",
          });
        }
      }
    } catch (error: any) {
      const parsedError = JSON.parse(error?.text || "{}");
      const results = parsedError?.data?.results || [];
      const summary = parsedError?.data?.summary || {};

      if (results.length > 0) {
        failCount = summary?.failed ?? results.length;

        const uniqueErrors = [...new Set(results.map((r: any) => r.error))];
        const errorMessage = uniqueErrors.join(" | ");

        failedSearchTermUids = results
          .map((failedTerm: any) => {
            const originalTerm = reqBody.terms.find(
              (t: any) => t.term === failedTerm.term,
            );
            return originalTerm?.additional_info?.search_term_shared_uid;
          })
          .filter(Boolean);

        messages.push({
          message:
            results.length === 1
              ? `Failed to add "${results[0]?.term}" as negative — ${errorMessage}`
              : `Failed to add ${results.length} negative terms — ${errorMessage}`,
          type: "error",
        });
      } else {
        // fallback for non-API errors
        failCount = reqBody.terms.length;
        failedSearchTermUids = reqBody.terms
          .map((t: any) => t.additional_info?.search_term_shared_uid)
          .filter(Boolean);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to add negative terms.";
        messages.push({
          message: `Error adding negative terms: ${errorMessage}`,
          type: "error",
        });
      }
    }

    return { successCount, failCount, failedSearchTermUids, messages };
  };

  // get Unclassified Search Terms
  const getSTPerformanceUnclassified = async () => {
    try {
      await STOperationEndPointLogic.getSTPerformanceUnclassified(
        oauthTokenHeader,
        QUERY_IDS.SEARCH_TERM_WITHOUT_CLASSIFICATION,
        selectedSharedUid,
        selectedAccountSharedUid,
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch unclassified search terms. Please try again.",
          type: "error",
        }),
      );
    }
  };

  // get Exclusive Missing Negatives Search Terms
  const getExclusiveSTMissingNegatives = async () => {
    try {
      await STOperationEndPointLogic.getESTMissingCrossNegatives(
        oauthTokenHeader,
        QUERY_IDS.EXCLUSIVE_MISSING_CROSS_NEGATIVES,
        selectedSharedUid,
        selectedAccountSharedUid,
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access missing cross negatives. Please try again.",
          type: "error",
        }),
      );
    }
  };

  // get Contradicting Search Terms Definitions
  const getContradictingSTDefinitions = async () => {
    try {
      await STOperationEndPointLogic.getContradictingSTDefinitions(
        oauthTokenHeader,
        QUERY_IDS.CONTRADICTING_SEARCH_TERM_DEFINITIONS,
        selectedSharedUid,
        selectedAccountSharedUid,
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access contradicting definitions. Please try again.",
          type: "error",
        }),
      );
    }
  };

  // get Identical to Exact Keyword Search Terms
  const getIdenticalToExactKeywordData = async () => {
    try {
      await STOperationEndPointLogic.getIdenticalToExactKeyword(
        oauthTokenHeader,
        QUERY_IDS.IDENTICAL_TO_EXACT_KEYWORD,
        selectedSharedUid,
        selectedAccountSharedUid,
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to access exact keyword data. Please try again.",
          type: "error",
        }),
      );
    }
  };

  // get Identical to Exact Keyword Search Terms
  const getCapturedByMultipleKeywords = async () => {
    try {
      await STOperationEndPointLogic.getCapturedByMultipleKeywords(
        oauthTokenHeader,
        QUERY_IDS.CAPTURED_BY_MULTIPLE_KEYWORDS,
        selectedSharedUid,
        selectedAccountSharedUid,
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access multiple search terms that are marked as Allowed. Please try again.",
          type: "error",
        }),
      );
    }
  };

  const activateOrdeactivateExistingLinks = async (deactivateReqBody: any) => {
    let successCount = 0;
    let failCount = 0;
    let successfulUids: string[] = []; // Track successful UIDs

    try {
      const response =
        await STOperationEndPointLogic.activeOrInactivePositiveLinks(
          deactivateReqBody,
          oauthTokenHeader,
        );

      if (response?.status === 200) {
        const results = response?.data?.data?.results || [];
        successCount = results.length;
        const sucessfulSahredUids = results.map(
          (result: any) => result.shared_uid,
        );
        successfulUids.push(...sucessfulSahredUids); // Add to successful UIDs
      }

      if (response?.status === 207) {
        const results = response?.data?.data?.results || [];
        const summary = response?.data?.data?.summary || {};
        if (summary?.success > 0) {
          successCount = summary.success;
          const sucessfulSahredUids = results
            .filter((result: any) => result.status === "success")
            .map((result: any) => result.shared_uid);
          successfulUids.push(...sucessfulSahredUids); // Add to successful UIDs
        }
      }
    } catch (error) {
      failCount++;
    }

    return { successCount, failCount, successfulUids };
  };

  return {
    fetchSearchTermsTabData,
    createPositiveSearchTermLinksMulti,
    createNegativeSearchTermLinks,
    getSTPerformanceUnclassified,
    getContradictingSTDefinitions,
    getExclusiveSTMissingNegatives,
    getIdenticalToExactKeywordData,
    getCapturedByMultipleKeywords,
    activateOrdeactivateExistingLinks,
  };
};
